import { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Loader2, Star, ChevronDown, ChevronRight } from 'lucide-react';
import { API, norm } from '@/lib/helpers';

export default function EscalasAuxView({ open, onClose, eventId, pessoas = [], auxGender = '' }) {
  const [functions, setFunctions] = useState([]);
  const [schedule, setSchedule] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [loading, setLoading] = useState(true);
  const [collapsedSection, setCollapsedSection] = useState(null);

  const pessoaMap = useMemo(() => {
    const m = {};
    for (const p of pessoas) m[p.pessoa_id] = p;
    return m;
  }, [pessoas]);

  const fetchScaleData = async (targetEventId) => {
    const [funcs, sched, attData] = await Promise.all([
      fetch(`${API}/service-functions`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
      fetch(`${API}/service-schedules/${targetEventId}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null),
      fetch(`${API}/attendance/event/${targetEventId}`, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
    ]);
    return { funcs, sched, attData };
  };

  useEffect(() => {
    if (!open || !eventId) return;
    let cancelled = false;
    setLoading(true);
    fetchScaleData(eventId).then(({ funcs, sched, attData }) => {
      if (cancelled) return;
      setFunctions(funcs.filter(f => f.active && !f.deleted));
      setSchedule(sched);
      setAttendance(attData);
      setLoading(false);
    }).catch(() => { if (!cancelled) setLoading(false); });
    // Initialize collapsed: opposite gender is collapsed
    setCollapsedSection(auxGender === 'masculino' ? 'feminino' : 'masculino');
    return () => { cancelled = true; };
  }, [open, eventId, auxGender]);

  const { femininas, masculinos } = useMemo(() => {
    if (!schedule || functions.length === 0) return { femininas: [], masculinos: [] };

    const funcMap = {};
    for (const f of functions) funcMap[f.function_id] = f;

    const attMap = {};
    const repLogMap = {};
    for (const a of attendance) {
      attMap[a.pessoa_id] = a.presente;
      if (!a.presente && a.replacement_log) {
        repLogMap[a.pessoa_id] = a.replacement_log;
      }
    }

    const leaderMap = schedule.leadership_map || {};

    const personFuncs = {};
    const addFunc = (pid, fid, fname, isLeader, redirectedTo) => {
      if (!personFuncs[pid]) personFuncs[pid] = [];
      if (!personFuncs[pid].some(f => f.function_id === fid)) {
        personFuncs[pid].push({ function_id: fid, function_name: fname, is_leader: isLeader, redirected_to: redirectedTo || null });
      }
    };

    for (const a of (schedule.assignments || [])) {
      const fid = a.function_id;
      const func = funcMap[fid];
      if (!func) continue;
      const fname = func.nome || a.function_name || '';
      for (const pid of [...(a.male || []), ...(a.female || [])]) {
        addFunc(pid, fid, fname, leaderMap[fid] === pid, null);
      }
    }

    for (const func of functions) {
      if (func.tipo !== 'recitativo' || !func.assigned_people) continue;
      for (const pid of func.assigned_people) {
        addFunc(pid, func.function_id, func.nome, leaderMap[func.function_id] === pid, null);
      }
    }

    for (const [pid, repLog] of Object.entries(repLogMap)) {
      if (!personFuncs[pid]) personFuncs[pid] = [];
      for (const r of repLog) {
        const existing = personFuncs[pid].find(f => f.function_name === r.function_name);
        if (existing) {
          existing.redirected_to = r.replacement_name || 'outro';
        } else {
          addFunc(pid, r.function_id || '', r.function_name, false, r.replacement_name || 'outro');
        }
      }
    }

    const allPids = Object.keys(personFuncs);
    const rows = allPids.map(pid => {
      const p = pessoaMap[pid];
      if (!p) return null;
      const nome = p.apelido || norm(p.nome_completo || '').split(' ')[0];
      const genero = (p.genero || '').toLowerCase();
      const att = attMap[pid];
      const funcs = (personFuncs[pid] || []).sort((a, b) => {
        const fa = funcMap[a.function_id];
        const fb = funcMap[b.function_id];
        const ta = fa?.tipo === 'recitativo' ? 0 : 1;
        const tb = fb?.tipo === 'recitativo' ? 0 : 1;
        return ta - tb || a.function_name.localeCompare(b.function_name);
      });
      return { pid, nome, genero, presente: att, funcs };
    }).filter(Boolean);

    // Sort: present first, then waiting, then absent last. Alphabetical within each group.
    const sortOrder = (att) => att === true ? 0 : att === null || att === undefined ? 1 : 2;
    rows.sort((a, b) => sortOrder(a.presente) - sortOrder(b.presente) || a.nome.localeCompare(b.nome));

    return {
      femininas: rows.filter(r => r.genero === 'feminino'),
      masculinos: rows.filter(r => r.genero === 'masculino'),
    };
  }, [schedule, functions, attendance, pessoaMap]);

  const dotClass = (presente) => {
    if (presente === true) return 'bg-green-500';
    if (presente === false) return 'bg-red-400';
    return 'bg-slate-300';
  };

  const renderRow = (row, gender) => {
    const badgeActive = gender === 'feminino'
      ? 'bg-pink-50 text-pink-700 border border-pink-200'
      : 'bg-blue-50 text-blue-700 border border-blue-200';

    return (
      <div key={row.pid} className="flex items-start gap-2 px-3 py-1.5 border-b border-slate-200 last:border-b-0" data-testid={`escala-aux-${row.pid}`}>
        <span className={`w-2 h-2 rounded-full flex-shrink-0 mt-[7px] sm:mt-[5px] ${dotClass(row.presente)}`} />
        <div className="flex flex-col sm:flex-row sm:items-center gap-0.5 sm:gap-2 sm:w-full min-w-0">
          <span className="text-sm text-slate-800 font-medium min-w-0 truncate">{row.nome}</span>
          <div className="flex items-center gap-1 flex-wrap sm:ml-auto">
          {row.funcs.map((f, i) => (
            f.redirected_to ? (
              <TooltipProvider key={i} delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge className="bg-slate-100 text-slate-400 border border-slate-200 text-[9px] line-through">{f.function_name}</Badge>
                  </TooltipTrigger>
                  <TooltipContent><p className="text-xs">Redirecionada para {f.redirected_to}</p></TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              <span key={i} className="relative inline-flex">
                <Badge className={`${badgeActive} text-[9px]`}>{f.function_name}</Badge>
                {f.is_leader && (
                  <TooltipProvider delayDuration={0}><Tooltip><TooltipTrigger asChild>
                    <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-amber-400 flex items-center justify-center ring-1 ring-white"><Star className="w-1.5 h-1.5 text-white fill-white" /></span>
                  </TooltipTrigger><TooltipContent><p className="text-xs">Líder desta função</p></TooltipContent></Tooltip></TooltipProvider>
                )}
              </span>
            )
          ))}
        </div>
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] flex flex-col p-0 sm:p-0 gap-0 overflow-hidden">
        <div className="px-5 pt-4 pb-2 border-b border-slate-100 flex-shrink-0">
          <DialogHeader className="p-0 space-y-0">
            <DialogTitle className="text-base">Escala do Evento</DialogTitle>
            <DialogDescription className="sr-only">Visualização da escala por auxiliar</DialogDescription>
          </DialogHeader>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto px-5 pt-1 pb-5">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-slate-400"><Loader2 className="w-5 h-5 animate-spin mr-2" />Carregando...</div>
          ) : (() => {
            const groups = auxGender === 'masculino'
              ? [{ key: 'masculino', label: 'Meninos', color: 'text-blue-500', rows: masculinos }, { key: 'feminino', label: 'Meninas', color: 'text-pink-500', rows: femininas }]
              : [{ key: 'feminino', label: 'Meninas', color: 'text-pink-500', rows: femininas }, { key: 'masculino', label: 'Meninos', color: 'text-blue-500', rows: masculinos }];
            return (
              <>
                {groups.map((g, gi) => g.rows.length > 0 && (
                  <div key={g.key} className={`border border-slate-200 rounded-lg bg-white overflow-hidden ${gi > 0 ? 'mt-3' : ''}`}>
                    <button
                      onClick={() => setCollapsedSection(prev => prev === g.key ? groups.find(x => x.key !== g.key)?.key : g.key)}
                      className="w-full flex items-center justify-between px-3 pt-2 pb-1 hover:bg-slate-50 transition-colors"
                    >
                      <p className={`text-[11px] font-semibold ${g.color} uppercase tracking-wider`}>{g.label}</p>
                      {collapsedSection === g.key ? <ChevronRight className="w-3.5 h-3.5 text-slate-400" /> : <ChevronDown className="w-3.5 h-3.5 text-slate-400" />}
                    </button>
                    {collapsedSection !== g.key && g.rows.map(r => renderRow(r, g.key))}
                  </div>
                ))}
                {femininas.length === 0 && masculinos.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">Nenhuma escala encontrada para este evento.</p>
                )}
              </>
            );
          })()}
        </div>
      </DialogContent>
    </Dialog>
  );
}
